<template>
  <div class="klv5-container">
    <div v-if="isLoading" class="state-block" data-state="loading">
      <v-progress-circular indeterminate color="#EA5545" size="64" />
      <p class="state-text">Leistungsfragen werden geladen...</p>
    </div>

    <template v-else>
      <div v-if="klarvertrag.length > 0" class="questions-container">
        <div class="pdf-export-section">
          <v-btn
            color="#EA5545"
            size="large"
            @click="generatePdf"
            prepend-icon="mdi-file-pdf-box"
            class="pdf-export-btn"
          >
            Als PDF exportieren
          </v-btn>
        </div>

        <v-card
          v-for="frage in klarvertrag"
          :key="frage.frage_id"
          class="question-card"
          elevation="1"
        >
          <v-card-text>
            <div class="question-header">
              <v-chip size="small" color="#EA5545" class="cluster-chip">
                Cluster {{ frage.frage_cluster }}: {{ frage.frage_sort }}
              </v-chip>
            </div>

            <h3 class="question-text">{{ frage.frage_text }}</h3>

            <div
              v-if="frage.options && frage.options.length > 0"
              class="options-container"
            >
              <div
                v-for="antwort in frage.options"
                :key="antwort.antwort_id"
                class="answer-item"
              >
                <div v-if="frage.frage_type == 1" class="answer-content">
                  <v-icon size="16" color="#4caf50" class="mr-2">mdi-text</v-icon>
                  <span v-html="nl2br(antwort.antwort_value)"></span>
                </div>

                <div v-else-if="frage.frage_type == 2" class="answer-content">
                  <v-icon size="16" color="#2196f3" class="mr-2">mdi-checkbox-marked-circle</v-icon>
                  <div class="option-details">
                    <span class="option-text">{{ antwort.option_text }}</span>
                    <div v-if="antwort.option_add_value == 1" class="option-value">
                      <strong v-html="nl2br(antwort.antwort_value)"></strong>
                      <span v-if="antwort.option_add_value_type == 1" class="value-unit"> Jahre</span>
                      <span v-else-if="antwort.option_add_value_type == 2" class="value-unit"> Zahn/Zähne</span>
                      <span v-else-if="antwort.option_add_value_type == 4" class="value-unit"> %</span>
                      <span v-else-if="antwort.option_add_value_type == 3" class="value-unit"> €</span>
                      <span v-else-if="antwort.option_add_value_type == 5" class="value-unit"> Monate</span>
                    </div>
                    <div v-if="antwort.option_punkte" class="option-points">
                      <v-icon size="14" color="#ff9800" class="mr-1">mdi-star</v-icon>
                      {{ antwort.option_punkte }} points
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="no-options">
              <v-icon size="20" color="#999">mdi-information-outline</v-icon>
              <span>No options available for this question</span>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div v-else class="state-block" data-state="empty">
        <v-icon size="80" color="#ccc">mdi-file-question</v-icon>
        <h3 class="state-title">No Questions Found</h3>
        <p class="state-text">
          There are no Klarvertrag questions for this product.
        </p>
      </div>
    </template>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, inject, onMounted, ref, watch } from 'vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  tarifId: {
    type: [Number, String],
    default: null
  }
})

const emit = defineEmits(['productLoaded'])

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')

const klarvertrag = ref([])
const isLoading = ref(false)
const lastLoadedId = ref(null)

const activeTarifId = computed(() => {
  if (props.tarifId !== null && props.tarifId !== undefined) {
    return Number(props.tarifId)
  }
  if (props.product?.prod_klv_id !== undefined) {
    return Number(props.product.prod_klv_id)
  }
  return null
})

const mypId = computed(() => props.product?.myp_id ?? null)

function nl2br(text) {
  if (!text) return ''
  return text.replace(/\n/g, '<br>')
}

function generatePdf() {
  if (!activeTarifId.value) return
  const url = `/clearmentget/generateklarvertragpdf/${activeTarifId.value}`
  window.open(url, '_blank')
}

async function loadKlarvertrag() {
  const tarifId = activeTarifId.value
  if (tarifId === null || tarifId === undefined) {
    klarvertrag.value = []
    return
  }

  if (lastLoadedId.value === tarifId) {
    return
  }

  isLoading.value = true
  try {
    const payload = {
      action: 'getklarvertrag',
      tarif_id: tarifId,
      limit: 100
    }
    if (mypId.value) {
      payload.myp_id = mypId.value
    }

    const response = await baseCalls.clearmentPost(payload)
    if (response.data.success && response.data.klarvertrag) {
      klarvertrag.value = response.data.klarvertrag
      emit('productLoaded', response.data.product || null)
      lastLoadedId.value = tarifId
    } else {
      klarvertrag.value = []
      emit('productLoaded', response.data.product || null)
      if (response.data.message) {
        errorStore?.setError?.(true, response.data.message)
      }
    }
  } catch (err) {
    console.error('Error loading Klarvertrag:', err)
    klarvertrag.value = []
    errorStore?.setError?.(true, 'Failed to load questions')
  } finally {
    isLoading.value = false
  }
}

watch(
  () => [activeTarifId.value, mypId.value],
  () => {
    lastLoadedId.value = null
    loadKlarvertrag()
  },
  { immediate: true }
)

onMounted(() => {
  if (activeTarifId.value !== null) {
    loadKlarvertrag()
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/modules/Klv5Zahn.css"></style>
